import { isEmpty, isNull } from "underscore";
import { VisitorBookingNewBookingBulkDetails } from "../containers/Visitors/BookingOverview/NewBooking/models/VisitorBookingNewBookingBulk";
import { ProductType } from "../models/visitor-booking/ProductType";
import { VisitorBookingProductConfiguration } from "../models/visitor-booking/VisitorBookingProductConfiguration";

const parseLocalDateTime = (date: string, time: string) => {
  const [day, month, year] = date.split("/").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};

const formatLocalDateTime = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const getParametersForGetProductSalesAvailability = (
  data: VisitorBookingNewBookingBulkDetails,
  products?: VisitorBookingProductConfiguration[] | null
) => {
  const product = products?.find((p) => p.id === data.productId);
  if(product && product.isProductManagementProduct){
    if (data.productType === ProductType.HourlyRate) {
      const startDateTime = `${data.startDate} ${data.startTime}`;
      const endDateTime = `${data.endDate} ${data.endTime}`;
      return [startDateTime, endDateTime];
    }
    else if (data.productType === ProductType.MultiDay)
    {
        const productStartTime = product?.startTime ?? "00:00";
        const startDateTime = `${data.startDate} ${productStartTime}`;
        const endDate = parseLocalDateTime(data.startDate, productStartTime);
        const recurrenceDays = data.recurrence ?? 0;
        endDate.setDate(endDate.getDate() + recurrenceDays);
        const endDateTime = formatLocalDateTime(endDate);

        return [startDateTime, endDateTime];
    }
    else if (data.productType === ProductType.FixedRate) {
      let productStartTime = product?.startTime ?? "00:00";
      let startDateTime = `${data.startDate} ${productStartTime}`;

      if (product.isFlexibleEntryAllowed) {
          startDateTime = `${data.startDate} ${data.startTime}`;
          productStartTime = data.startTime;
      }

      if (product?.duration) {
        const parkingDuration = product.isFlexibleEntryAllowed && product.parkingTime ? product.parkingTime : product.duration;
        const endDate = parseLocalDateTime(data.startDate, productStartTime);
        endDate.setMinutes(endDate.getMinutes() + parkingDuration);
        const endDateTime = formatLocalDateTime(endDate);

        return [startDateTime, endDateTime];
      }

      return [startDateTime, startDateTime];
    }
    
    const startDateTime = `${data.startDate} 00:00`;
    const endDateTime = startDateTime;
    return [startDateTime, endDateTime];
  } else {
    const isHourlyRateProduct = data.productType === ProductType.HourlyRate;
    const startDateTime = isHourlyRateProduct
      ? `${data.startDate} ${data.startTime}`
      : `${data.startDate} 00:00`;
    const endDateTime = isHourlyRateProduct ? `${data.endDate} ${data.endTime}` : startDateTime;
    return [startDateTime, endDateTime];
  }
};

export const isTimeframeCorrectlySet = (data: VisitorBookingNewBookingBulkDetails) => {
  const isHourlyRateProduct = data.productType === ProductType.HourlyRate;
  const isFlexibleFixedRateProduct = data.productType === ProductType.FixedRate && data.isFlexibleEntryAllowed;
  const startDate = data.startDate;
  const endDate = data.endDate;
  const startTime = data.startTime;
  const endTime = data.endTime;

  const isFixedRateTimeProperlySelected =
    !isHourlyRateProduct && !isFlexibleFixedRateProduct && !isEmpty(startDate) && !isNull(startDate);
  const isFlexibleFixedRateTimeProperlySelected =
    isFlexibleFixedRateProduct &&
    !isEmpty(startDate) &&
    !isNull(startDate) &&
    !isEmpty(startTime) &&
    !isNull(startTime);
  const isHourlyRateTimeProperlySelected =
    isHourlyRateProduct &&
    !isEmpty(startDate) &&
    !isNull(startDate) &&
    !isEmpty(endDate) &&
    !isNull(endDate) &&
    !isEmpty(startTime) &&
    !isNull(startTime) &&
    !isEmpty(endTime) &&
    !isNull(endTime);

  return isHourlyRateTimeProperlySelected || isFixedRateTimeProperlySelected || isFlexibleFixedRateTimeProperlySelected;
};

export const isParkingLocationSet = (physicalZone?: string) =>
  !isNull(physicalZone) &&
  !isEmpty(physicalZone);

export const performOnChange = (data: VisitorBookingNewBookingBulkDetails) =>
  !isNull(data.productId) &&
  !isEmpty(data.productId) &&
  isParkingLocationSet(data.physicalZone);